import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationStore } from '../../states/auth/auth.store';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { SetupAttentionService } from '../setup/setup-attention.service';

interface WikiAttentionState {
  acknowledged: boolean;
  acknowledgedAt?: string;
}

/**
 * Wiki intro modal:
 * - Auto-shows once per login session when landing on a welcome/dashboard route
 * - Does not re-show when navigating back to HR dashboard in the same session
 * - "Don't show again" persists across sessions (localStorage)
 * - Manual open via top-bar ? still works
 */
@Injectable({ providedIn: 'root' })
export class WikiAttentionService {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthenticationStore);
  private readonly userProfileStore = inject(UserProfileStore);
  private readonly setupAttentionService = inject(SetupAttentionService);

  private readonly storagePrefix = 'trieonhr_wiki_attention';
  private readonly currentUrl = signal(this.router.url);
  /** In-memory mirror of sessionStorage so signals update immediately. */
  private readonly seenThisSession = signal(false);
  /** Kept so logout can clear sessionStorage after the profile is wiped. */
  private lastUserGuid: string | null = null;

  readonly modalVisible = signal(false);

  readonly isEligibleUser = computed(() => {
    const profile = this.userProfileStore.userProfile();
    if (!profile) {
      return false;
    }

    if (this.userProfileStore.roleName().trim().toLowerCase() === 'employee') {
      return false;
    }

    return (profile.roleFeatures ?? []).some((feature) =>
      String(feature.routeUrl ?? '').startsWith('/features'),
    );
  });

  readonly shouldAutoShow = computed(() => {
    if (!this.isEligibleUser() || this.seenThisSession() || this.isAcknowledged()) {
      return false;
    }

    if (this.setupAttentionService.modalVisible()) {
      return false;
    }

    return this.isWelcomeAdminRoute(this.currentUrl());
  });

  constructor() {
    this.hydrateSeenThisSession();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.evaluateAutoShow();
      });

    effect(() => {
      if (!this.authStore.isAuthenticated()) {
        this.clearSessionPrompt();
        this.modalVisible.set(false);
        return;
      }

      this.hydrateSeenThisSession();
      this.setupAttentionService.modalVisible();
      this.currentUrl();
      this.seenThisSession();
      this.isEligibleUser();
      this.evaluateAutoShow();
    });

    this.evaluateAutoShow();
  }

  closeForSession(): void {
    this.markSeenThisSession();
    this.modalVisible.set(false);
  }

  acknowledgePermanently(): void {
    this.saveAcknowledgement();
    this.markSeenThisSession();
    this.modalVisible.set(false);
  }

  showModal(): void {
    if (this.isEligibleUser()) {
      this.modalVisible.set(true);
    }
  }

  private evaluateAutoShow(): void {
    if (!this.shouldAutoShow()) {
      return;
    }
    this.modalVisible.set(true);
    // Mark immediately so returning to the dashboard this session won't reopen it.
    this.markSeenThisSession();
  }

  private hydrateSeenThisSession(): void {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid;
    if (!userGuid) {
      this.seenThisSession.set(false);
      return;
    }
    this.lastUserGuid = userGuid;
    this.seenThisSession.set(sessionStorage.getItem(this.sessionStorageKey(userGuid)) === '1');
  }

  private markSeenThisSession(): void {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid ?? this.lastUserGuid;
    if (!userGuid) {
      this.seenThisSession.set(true);
      return;
    }
    this.lastUserGuid = userGuid;
    sessionStorage.setItem(this.sessionStorageKey(userGuid), '1');
    this.seenThisSession.set(true);
  }

  private clearSessionPrompt(): void {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid ?? this.lastUserGuid;
    if (userGuid) {
      sessionStorage.removeItem(this.sessionStorageKey(userGuid));
    }
    this.lastUserGuid = null;
    this.seenThisSession.set(false);
  }

  private sessionStorageKey(userGuid: string): string {
    return `${this.storagePrefix}_session_${userGuid}`;
  }

  private isAcknowledged(): boolean {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid;
    if (!userGuid) {
      return false;
    }

    const raw = localStorage.getItem(`${this.storagePrefix}_${userGuid}`);
    if (!raw) {
      return false;
    }

    try {
      const state = JSON.parse(raw) as WikiAttentionState;
      return state.acknowledged === true;
    } catch {
      return false;
    }
  }

  private saveAcknowledgement(): void {
    const userGuid = this.userProfileStore.userProfile()?.userProfileGuid;
    if (!userGuid) {
      return;
    }

    const state: WikiAttentionState = {
      acknowledged: true,
      acknowledgedAt: new Date().toISOString(),
    };
    localStorage.setItem(`${this.storagePrefix}_${userGuid}`, JSON.stringify(state));
  }

  private isWelcomeAdminRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return (
      path === '/features' ||
      path === '/features/payroll' ||
      path === '/features/hr/dashboard'
    );
  }
}
